import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Switch, Route } from 'react-router'
import { withRouter } from 'react-router-dom'
import loadable from '@loadable/component'
import { VocabProvider } from '@sainsburys-tech/bolt-vocab-provider'
import { Lab } from '@sainsburys-tech/dexters-lab'
import { ThemeProvider } from 'styled-components'
import LazyHydrate from 'react-lazy-hydration'

import * as brandsConfig from '../providers/BrandConfigContext/config/coreBrandConfs'
import { BrandContextProvider } from '../providers/BrandConfigContext/BrandConfigContext'
import featureBrandConfigs from '../providers/BrandConfigContext/config/featureBrandConfigs'

import ArgosHeader from '../containers/Header/Header'
import PDPFooter from '../containers/Footer/Footer'

import updateDigitalData, { doTrack } from '../helpers/taggingSerializer'
import { CONSENT_CATEGORIES } from '../config/cookie-consent'
import { startCheckingForMarketingConsent, stopCheckingForMarketingConsent } from '../helpers/checkCookieConsent'

import { pdp0, specialoffers, questions, reviews } from '../routes'

import GlobalStyle from '../GlobalStyle'
import { FeatureFlagContextProvider } from '../providers/FeatureFlagContext/FeatureFlagContext'

const BRAND = process.env.BRAND || 'ARGOS'
const brandConfig = brandsConfig[BRAND.toLocaleLowerCase()]

const questionsHandler = loadable(() =>
  import(/* webpackChunkName: "questions-handler" */ '../containers/QuestionsPage/QuestionsPage')
)

const reviewsHandler = loadable(() =>
  import(/* webpackChunkName: "questions-handler" */ '../containers/ReviewsPage/ReviewsPage')
)

const specialOffersHandler = loadable(() =>
  import(/* webpackChunkName: "special-offers-handler" */ '../containers/SpecialOffersPage/SpecialOffersPage')
)

const productPageHandler = loadable(() =>
  import(/* webpackChunkName: "product-page-handler" */ '../containers/ProductPage')
)

const errorPageHandler = loadable(() => import(/* webpackChunkName: "error-page-handler" */ './ErrorPage/ErrorPage'))

const FableAddToTrolleyDrawer = loadable(() =>
  import(/* webpackChunkName: "add-to-trolley" */ './FableAddToTrolleyDrawer/FableAddToTrolleyDrawer')
)

const isTuProduct = partNumber =>
  partNumber
    .trim()
    .toLowerCase()
    .startsWith('tuc')

const getTemplate = (productId, taxonomyTemplateType, isWarrantyItem) => {
  if (isTuProduct(productId)) return 'TuClothing'
  if (taxonomyTemplateType) return taxonomyTemplateType
  return isWarrantyItem ? 'AfsProductCare' : ''
}

class Application extends Component {
  componentDidMount() {
    const stores = {
      productStore: this.props.productStore,
      bazaarVoiceReviewStore: this.props.bazaarVoiceReviewStore,
      bazaarVoiceQuestionAnswerStore: this.props.bazaarVoiceQuestionAnswerStore,
      storeLocatorStore: this.props.storeLocator,
      alternativeProductStore: this.props.alternativesStore,
      specialOffersCreatorStore: this.props.specialOffersCreator,
      essentialExtrasStore: this.props.essentialExtrasStore
    }

    if (BRAND === 'TU') {
      startCheckingForMarketingConsent({
        brand: BRAND,
        consentCategory: CONSENT_CATEGORIES.PERFORMANCE,
        noConsentCallback: () => {
          if (typeof window?.BV?.pixel?.doNotTrack === 'function') window.BV.pixel.doNotTrack()
        }
      })
    }

    const { productStore, specialOffersCreatorStore } = stores
    if (specialOffersCreatorStore && specialOffersCreatorStore.promotionId) {
      updateDigitalData(stores, 'Initial special offers load')
      doTrack('so_newPage')
    } else if (!productStore.meta.errorCode && !productStore.meta.errorDetail) {
      updateDigitalData(stores, 'Initial load')
      if (BRAND === 'HABITAT' || BRAND === 'TU') {
        doTrack('pdp_initialLoad')
      }
    } else if (productStore.meta.errorCode) {
      updateDigitalData(stores, `Initial ${productStore.meta.errorCode} load`)
      if (BRAND === 'HABITAT' || BRAND === 'TU') {
        doTrack(`pdp_${productStore.meta.errorCode}`)
      }
    }
  }

  componentDidUpdate(prevProps) {
    const newProps = this.props
    if (newProps.pageTitle === prevProps.pageTitle) {
      return
    }
    document.title = newProps.pageTitle
  }

  componentWillUnmount() {
    stopCheckingForMarketingConsent()
  }

  getBrandContextBrand() {
    if (BRAND === 'ARGOS') {
      const { productId, templateType, attributes } = this.props.productStore.data
      const isWarrantyItem = (attributes && attributes.isWarrantyItem) || false
      const template = getTemplate(productId, templateType, isWarrantyItem)
      if (template) {
        return `${template.replace(/\s/g, '')}OnArgos`
      }
    }
    return BRAND
  }

  render() {
    const { productStore } = this.props

    const featureBrandConfig =
      featureBrandConfigs[this.getBrandContextBrand().toLowerCase()] || featureBrandConfigs[BRAND.toLowerCase()] // Fallback to the brand config if specific feature config is not found

    const { FABLE_ATT, PDP_V2_ENABLED } = featureBrandConfig

    const PDP_V2_FLAG = PDP_V2_ENABLED

    const isCategoryExperience =
      featureBrandConfig?.CATEGORY_EXPERIENCES_TEMPLATE_ENABLED || productStore?.data?.attributes?.madeToOrder

    // Check PDP V2 flag is enabled and is not a category experience template
    const featureFlagContextValues = {
      PDP_V2_FLAG: PDP_V2_FLAG && !isCategoryExperience
    }
    return (
      <Lab dangerousNonFlicker renderOnServer>
        <ThemeProvider theme={brandConfig.theme}>
          <VocabProvider vocab={brandConfig.vocab}>
            <BrandContextProvider brand={this.getBrandContextBrand()}>
              <FeatureFlagContextProvider values={featureFlagContextValues}>
                <GlobalStyle />
                {FABLE_ATT && <FableAddToTrolleyDrawer brand={BRAND.toLowerCase()} />}
                <div className={brandConfig?.bolt?.namespace}>
                  {BRAND === 'ARGOS' && <ArgosHeader />}
                  <div id='content'>
                    <Switch>
                      <Route path={questions.path} component={questionsHandler} />
                      <Route path={reviews.path} component={reviewsHandler} />
                      <Route path={specialoffers.path} component={specialOffersHandler} />
                      <Route path={pdp0.path} component={productPageHandler} />
                      <Route component={errorPageHandler} />
                    </Switch>
                  </div>
                  <LazyHydrate whenIdle>
                    <PDPFooter />
                  </LazyHydrate>
                </div>
              </FeatureFlagContextProvider>
            </BrandContextProvider>
          </VocabProvider>
        </ThemeProvider>
      </Lab>
    )
  }
}

const mapStateToProps = state => {
  const {
    storeLocator,
    bazaarVoiceReview = {},
    bazaarVoiceQuestionAnswer = {},
    productStore = {},
    alternatives = {},
    specialOffersCreator = {},
    essentialExtras = {}
  } = state

  const {
    data: { productId }
  } = productStore

  const bazaarVoiceReviewStore = bazaarVoiceReview.data || {}
  const bazaarVoiceQuestionAnswerStore = bazaarVoiceQuestionAnswer.data || {}
  const {
    data: { [productId]: alternativesStore }
  } = alternatives
  const { data: essentialExtrasStore } = essentialExtras

  const props = {
    storeLocator: storeLocator.data,
    bazaarVoiceReviewStore,
    bazaarVoiceQuestionAnswerStore,
    productStore,
    pageTitle: productStore.data.productName,
    alternativesStore,
    specialOffersCreator: specialOffersCreator.data,
    essentialExtrasStore
  }

  return props
}

Application.propTypes = {
  allCookies: PropTypes.shape({
    PDP_Test_Group_1: PropTypes.string
  })
}

const mapDispatchToProps = {}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Application))
export { Application, mapStateToProps, getTemplate, isTuProduct }
